import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { getPreference, setPreference } from '@fs/zion-preferences'
import {
  MemoriesApiContext,
  MemoriesProvider,
  memoryUtils,
  useMemoriesApi,
  useMemoriesPermissions,
  sessionKeys,
} from '@fs/zion-memories/memories-authorized-use-only'
import { useAlbums, useAlbumsApi } from './useAlbums'

const GALLERY_SORT_PREFERENCE_KEY = 'memories.gallery-sort'
const ALBUM_SORT_PREFERENCE_KEY = 'memories.album-sort'

const DEFAULT_SORT = { sortType: 'dateAdded', sortAscending: true }

function parseSortPreference(storedSort) {
  if (!storedSort) return null
  // Handle legacy string value stored before object format was introduced
  if (typeof storedSort === 'string') return { sortType: storedSort, sortAscending: true }
  return storedSort
}

function SortPreferenceProvider({ children, isAlbumPage }) {
  const memoriesApi = useMemoriesApi()
  const preferenceLoadedRef = useRef(false)
  const loadedSortsRef = useRef({})

  const preferenceKey = isAlbumPage ? ALBUM_SORT_PREFERENCE_KEY : GALLERY_SORT_PREFERENCE_KEY

  const saveSortPreference = useCallback(
    (sortType, sortAscending) => {
      if (loadedSortsRef.current[preferenceKey]) {
        loadedSortsRef.current[preferenceKey] = { sortType, sortAscending }
      }
      setPreference(preferenceKey, { sortType, sortAscending }).catch((error) => {
        console.error('Error saving sort preference:', error)
      })
    },
    [preferenceKey]
  )

  const setSortType = useCallback(
    (type) => {
      memoriesApi.setSortType(type)
      if (!preferenceLoadedRef.current) return
      // Never persist 'custom' to the gallery preference — it's only valid within albums
      if (!isAlbumPage && type === 'custom') return
      const sortAscending = loadedSortsRef.current[preferenceKey]?.sortAscending ?? true
      saveSortPreference(type, sortAscending)
    },
    [memoriesApi, preferenceKey, isAlbumPage, saveSortPreference]
  )

  const setSortAscending = useCallback(
    (ascending) => {
      memoriesApi.setSortAscending(ascending)
      if (!preferenceLoadedRef.current) return
      const sortType = loadedSortsRef.current[preferenceKey]?.sortType ?? 'dateAdded'
      saveSortPreference(sortType, ascending)
    },
    [memoriesApi, preferenceKey, saveSortPreference]
  )

  useEffect(() => {
    preferenceLoadedRef.current = false

    // On subsequent visits to this view, apply the cached sort synchronously to avoid a flash
    if (preferenceKey in loadedSortsRef.current) {
      const { sortType, sortAscending } = loadedSortsRef.current[preferenceKey]
      setSortType(sortType)
      setSortAscending(sortAscending)
      preferenceLoadedRef.current = true
      return
    }

    const loadSortPreference = async () => {
      try {
        const storedSort = await getPreference(preferenceKey)
        const parsed = parseSortPreference(storedSort)
        // 'custom' is only valid for albums — ignore it if saved to the gallery key from old code
        const sortType = !isAlbumPage && parsed?.sortType === 'custom' ? 'dateAdded' : (parsed?.sortType ?? 'dateAdded')
        const sortAscending = parsed?.sortAscending ?? true
        loadedSortsRef.current[preferenceKey] = { sortType, sortAscending }
        setSortType(sortType)
        setSortAscending(sortAscending)
      } catch (error) {
        console.error('Error loading sort preference:', error)
        loadedSortsRef.current[preferenceKey] = { ...DEFAULT_SORT }
        setSortType(DEFAULT_SORT.sortType)
        setSortAscending(DEFAULT_SORT.sortAscending)
      } finally {
        preferenceLoadedRef.current = true
      }
    }
    loadSortPreference()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run when view switches between gallery and album
  }, [preferenceKey])

  const patchedApi = useMemo(
    () => ({ ...memoriesApi, setSortType, setSortAscending }),
    [memoriesApi, setSortType, setSortAscending]
  )

  return <MemoriesApiContext.Provider value={patchedApi}>{children}</MemoriesApiContext.Provider>
}

export default function MemoriesProviderWrapper({
  children,
  galleryCallbacks,
  galleryMemories,
  memoriesDirty,
  memoriesLoading,
  setMemoriesDirty,
  isAlbumPage,
}) {
  const { albumMemories, selectedAlbum, selectedAlbumId } = useAlbums()
  const {
    addAlbum,
    addCallback,
    addToAlbumsCallback,
    deleteCallback,
    isMemoryInAnyAlbum,
    removeFromAlbumCallback,
    updateCallback,
  } = useAlbumsApi()
  const { isAdmin, isAlbumOwner } = useMemoriesPermissions()
  const { isPrivate } = memoryUtils

  const callbacks = useMemo(
    () => ({
      ...galleryCallbacks,
      addAlbum,
      addCallback,
      addToAlbumsCallback,
      deleteCallback,
      isMemoryInAnyAlbum,
      removeFromAlbumCallback,
      setMemoriesDirty,
      updateCallback,
    }),
    [
      addAlbum,
      addCallback,
      addToAlbumsCallback,
      deleteCallback,
      galleryCallbacks,
      isMemoryInAnyAlbum,
      removeFromAlbumCallback,
      setMemoriesDirty,
      updateCallback,
    ]
  )

  let memories = selectedAlbumId ? albumMemories : galleryMemories

  // Hide private memories when viewing albums you don't own, unless you're an admin
  if (isAlbumPage && !isAlbumOwner(selectedAlbum) && !isAdmin) {
    memories = memories.filter((memory) => !isPrivate(memory))
  }

  return (
    <MemoriesProvider
      galleryCallbacks={callbacks}
      memories={memories}
      memoriesDirty={memoriesDirty}
      memoriesLoading={memoriesLoading}
      sessionStorageKey={sessionKeys.gallerySelectedFilters}
    >
      <SortPreferenceProvider isAlbumPage={isAlbumPage}>{children}</SortPreferenceProvider>
    </MemoriesProvider>
  )
}
